import { modelsApi } from '@/api/models.api';
import { projectsApi } from '@/api/projects.api';
import { CircularProgress } from '@/components/common/CircularProgress';
import { sanitizeFileName, validateStpFile } from '@/utils/validators';
import { Plus, Upload } from 'lucide-react';
import type { FC } from 'react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

type UploadPhase = 'creating' | 'uploading' | 'processing';

interface CreateProjectCardProps {
  compact?: boolean;
  /** When provided, upload runs in background via queue; card stays ready for next upload and no redirect. */
  onStartUpload?: (file: File) => void;
}

export const CreateProjectCard: FC<CreateProjectCardProps> = ({ compact = false, onStartUpload }) => {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadPhase, setUploadPhase] = useState<UploadPhase>('creating');
  const [currentStep, setCurrentStep] = useState<string>('');
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isUploading) return;
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropZoneRef.current && e.relatedTarget instanceof Node) {
      if (dropZoneRef.current.contains(e.relatedTarget)) return;
    }
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const pickFile = (file: File): boolean => {
    const validation = validateStpFile(file);
    if (!validation.valid) {
      toast.error(validation.error);
      return false;
    }
    return true;
  };

  const handleFileChosen = async (file: File) => {
    if (!pickFile(file)) return;

    if (onStartUpload) {
      onStartUpload(file);
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setUploadPhase('creating');
    let projectId: string | null = null;

    try {
      const title = sanitizeFileName(file.name);
      const { uid } = await projectsApi.create({ title });
      projectId = uid;

      setUploadPhase('uploading');
      setCurrentStep(t('projects.uploadingEmbedded'));

      let taskId: string;
      try {
        const submit = await modelsApi.uploadEmbeddedBackground(projectId, file);
        taskId = submit.uid;
      } catch (submitError) {
        await projectsApi.delete(projectId).catch(() => undefined);
        projectId = null;
        throw submitError;
      }

      await modelsApi.trackProgressSSE(taskId, (p) => {
        setUploadProgress(p.progress);
        setCurrentStep(p.current_step || t('projects.uploadingEmbedded'));
        setUploadPhase(p.progress < 10 ? 'uploading' : 'processing');
      });

      toast.success(t('projects.projectCreated'));
      setTimeout(() => {
        if (projectId) navigate(`/project/${projectId}`);
      }, 300);
    } catch (error) {
      console.error('Error during project creation/upload:', error);

      let errorMessage = projectId ? t('projects.failedToUpload') : t('projects.failedToCreate');

      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as {
          response?: { status?: number; data?: { detail?: string } };
          code?: string;
        };
        const status = axiosError.response?.status;
        const code = axiosError.code;
        const detail = axiosError.response?.data?.detail;

        if (code === 'ECONNREFUSED' || code === 'ENOTFOUND' || code === 'ERR_NETWORK' || code === 'ECONNABORTED') {
          errorMessage = t('projects.backendUnavailable');
        } else if (status === 400 && detail) {
          // Backend embedded-stock validation rejection (no "stock" solid,
          // model protrudes beyond stock, unsupported colors, …) — surface verbatim.
          errorMessage = detail;
        } else if (status && status >= 500 && status < 600) {
          errorMessage = t('projects.serverError');
        } else if (status === 502 || status === 503 || status === 504) {
          errorMessage = t('projects.backendUnavailable');
        } else if (status === 401) {
          errorMessage = t('projects.authError');
        } else if (status === 404) {
          errorMessage = projectId ? t('projects.uploadError') : t('projects.failedToCreate');
        } else if (detail) {
          errorMessage = detail;
        }
      } else if (error instanceof Error) {
        const message = error.message.toLowerCase();
        if (message.includes('apt generation failed')) {
          errorMessage = error.message.replace(/^Failed:\s*/, '');
        } else if (
          message.includes('network') ||
          message.includes('fetch') ||
          message.includes('connection') ||
          message.includes('econnrefused') ||
          message.includes('enotfound') ||
          message.includes('err_network')
        ) {
          errorMessage = projectId ? t('projects.uploadConnectionError') : t('projects.createConnectionError');
        } else if (message.includes('timeout') || message.includes('too long') || message.includes('timed out')) {
          errorMessage = projectId ? t('projects.uploadTimeout') : t('projects.createTimeout');
        } else if (message.includes('401') || message.includes('unauthorized') || message.includes('authentication')) {
          errorMessage = t('projects.authError');
        }
      }

      toast.error(errorMessage);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      setCurrentStep('');
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (isUploading) {
      toast.warning(t('projects.uploadInProgress'));
      return;
    }
    const file = Array.from(e.dataTransfer.files).find((f) => f.name.toLowerCase().endsWith('.stp'));
    if (!file) {
      toast.error(t('validation.stpFileRequired'));
      return;
    }
    void handleFileChosen(file);
  };

  const handleClick = () => {
    if (isUploading) return;
    fileInputRef.current?.click();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    void handleFileChosen(file);
  };

  return (
    <>
      <div
        ref={dropZoneRef}
        className={`bg-white rounded-lg shadow border-2 border-dashed ${
          compact ? 'p-4 min-h-[120px]' : 'p-8 min-h-[300px]'
        } ${
          isDragging
            ? 'border-primary bg-primary/5'
            : isUploading
              ? 'border-primary bg-primary/5 animate-pulse'
              : 'border-gray-300 hover:border-primary'
        } ${isUploading ? '' : 'hover:shadow-lg'} transition-all ${isUploading ? 'cursor-wait' : 'cursor-pointer'} flex flex-col items-center justify-center gap-4`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleClick();
          }
        }}
        tabIndex={0}
        role="button"
      >
        {isUploading ? (
          <div className={`w-full ${compact ? 'max-w-sm' : 'max-w-md'} space-y-4`}>
            <div className="flex items-center justify-center">
              <CircularProgress
                progress={uploadPhase === 'creating' ? 0 : uploadProgress}
                size={compact ? 'medium' : 'large'}
                showPercentage={true}
              />
            </div>
            <div className="space-y-2">
              <p className={`${compact ? 'text-xs' : 'text-sm'} font-medium text-gray-900 text-center`}>
                {uploadPhase === 'creating' && t('projects.creating')}
                {uploadPhase === 'uploading' && t('projects.uploadingEmbedded')}
                {uploadPhase === 'processing' &&
                  (currentStep === 'Generating APT hole visualizations'
                    ? t('projects.generatingAptHoleVisualizations')
                    : currentStep || t('projects.processing'))}
              </p>
            </div>
            {!compact && (
              <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
                <div className="h-1.5 w-1.5 bg-primary rounded-full animate-pulse" />
                <span>{t('projects.mayTakeTime')}</span>
                <div className="h-1.5 w-1.5 bg-primary rounded-full animate-pulse" />
              </div>
            )}
          </div>
        ) : (
          <>
            <div className={`rounded-full bg-gray-100 ${compact ? 'p-3' : 'p-6'}`}>
              {isDragging ? (
                <Upload size={compact ? 24 : 48} className="text-primary" />
              ) : (
                <Plus size={compact ? 24 : 48} className="text-gray-400" />
              )}
            </div>
            <div className="text-center">
              <h3
                className={`${compact ? 'text-sm' : 'text-lg'} font-semibold text-gray-900 ${compact ? 'mb-0.5' : 'mb-1'}`}
              >
                {isDragging ? t('projects.dropHere') : t('projects.addNew')}
              </h3>
              {!compact && <p className="text-sm text-gray-500">{t('projects.clickToOpen')}</p>}
            </div>
          </>
        )}
      </div>

      <input ref={fileInputRef} type="file" accept=".stp" className="hidden" onChange={handleInputChange} />
    </>
  );
};
